import Image from "next/image";
import { useCallback, useMemo, useState } from "react";
import { Api } from "../../../../services/api";
import debounce from "../../../../utils/debounce";
import { PlaceSuggestionsResponse } from "../../../../utils/interfaces";
import { BoxedInput } from "../../../../components/forms";
import { BottomSheet } from "../../../../components/modals/bottom-sheet/bottom-sheet";

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onSelectAddress: (placeId: string) => void;
}

export const LocationSheet = ({
  visible,
  onDismiss,
  onSelectAddress,
}: Props) => {
  const api = useMemo(() => {
    const _api = new Api();
    _api.setup();
    return _api;
  }, []);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [placeSuggestions, setPlaceSuggestions] =
    useState<PlaceSuggestionsResponse[]>(null);

  const getAddressSuggestions = useCallback(
    async (addressVal = searchQuery) => {
      if (!addressVal) return;
      const result = await api.getPlaceSuggestions(addressVal);
      if (result?.predictions?.length) {
        setPlaceSuggestions(result.predictions);
      }
    },
    [api, searchQuery]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchQuery = useCallback(
    debounce(
      async (addressVal: string) => await getAddressSuggestions(addressVal),
      500
    ),
    []
  );

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    debouncedSearchQuery(val);
  };

  const handleSelectAddress = (placeId: string) => {
    onSelectAddress(placeId);
    onDismiss();
  };

  return (
    <BottomSheet visible={visible} onDismiss={onDismiss}>
      <div className="h-[425px] relative z-[60]">
        <BoxedInput
          placeholder="Cari alamat"
          value={searchQuery}
          onChange={handleSearchChange}
          autoFocus
          className="mb-4 bg-gray-100 border-gray-100"
          prepend={
            <Image
              src="/img/icons/search.svg"
              alt="Search"
              width={16}
              height={16}
            />
          }
        />
        <div className="overflow-auto h-[315px]">
          {placeSuggestions?.length > 0 &&
            placeSuggestions.map((place) => (
              <div
                key={place.place_id}
                onClick={() => handleSelectAddress(place.place_id)}
                className="py-2 flex items-start cursor-pointer"
              >
                <div className="shrink-0 mr-2">
                  <Image
                    src={`/img/${process.env.NEXT_PUBLIC_APP_NAME}/icons/map-marker.svg`}
                    alt="Location"
                    width={24}
                    height={24}
                  />
                </div>
                <div className="flex flex-col">
                  <span className="block font-bold">
                    {place.structured_formatting.main_text}
                  </span>
                  <p>{place.description}</p>
                </div>
              </div>
            ))}
        </div>
        <div className="absolute bottom-0 inset-x-0">
          <button
            onClick={onDismiss}
            className="w-full rounded py-2 hover:bg-gray-50 text-primary"
          >
            BATAL
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};
